import { z } from 'zod';

/**
 * Every ISO 3166-1 alpha-2 country / territory with its region (UN M49 continents; the Americas split into
 * North, Central, Caribbean and South) and EU membership. Names are not stored: clients use
 * `Intl.DisplayNames` in their own language. Sites pick allowed countries from this list (quick picks by
 * region or the EU); a launch's declared `country` must be one of these codes.
 */
export type Region = 'AFRICA' | 'AMERICAS' | 'ASIA' | 'EUROPE' | 'OCEANIA' | 'ANTARCTICA';
export type AmericasPart = 'NORTH' | 'CENTRAL' | 'CARIBBEAN' | 'SOUTH';
export interface Country {
  code: string;
  region: Region;
  /** Only for the Americas. */
  part?: AmericasPart;
  eu: boolean;
}

const list = (s: string) => s.trim().split(/\s+/);
const AFRICA = list('DZ AO BJ BW BF BI CV CM CF TD KM CG CD CI DJ EG GQ ER SZ ET GA GM GH GN GW KE LS LR LY MG MW ML MR MU MA MZ NA NE NG RW ST SN SC SL SO ZA SS SD TZ TG TN UG ZM ZW EH RE YT SH IO TF');
const AMERICAS: Record<AmericasPart, string[]> = {
  NORTH: list('US CA MX GL BM PM'),
  CENTRAL: list('BZ CR SV GT HN NI PA'),
  CARIBBEAN: list('AG BS BB CU DM DO GD HT JM KN LC VC TT AI AW BQ VG KY CW GP MQ MS PR BL MF SX TC VI'),
  SOUTH: list('AR BO BR CL CO EC GY PY PE SR UY VE FK GF GS BV'),
};
export const EU_MEMBERS = list('AT BE BG HR CY CZ DK EE FI FR DE GR HU IE IT LV LT LU MT NL PL PT RO SK SI ES SE');
const EUROPE_OTHER = list('AX AL AD BY BA FO GI GG VA IS IM JE LI MD MC ME MK NO RU SM RS SJ UA GB CH');
const ASIA = list('AF AM AZ BH BD BT BN KH CN GE HK IN ID IR IQ IL JP JO KZ KW KG LA LB MO MY MV MN MM NP KP OM PK PS PH QA SA SG KR LK SY TW TJ TH TL TR TM AE UZ VN YE');
const OCEANIA = list('AS AU CK FJ PF GU KI MH FM NR NC NZ NU NF MP PW PG PN WS SB TK TO TV UM VU WF CX CC HM');

export const COUNTRIES: readonly Country[] = [
  ...AFRICA.map((code) => ({ code, region: 'AFRICA' as const, eu: false })),
  ...(Object.entries(AMERICAS) as [AmericasPart, string[]][]).flatMap(([part, codes]) => codes.map((code) => ({ code, region: 'AMERICAS' as const, part, eu: false }))),
  ...EU_MEMBERS.map((code) => ({ code, region: 'EUROPE' as const, eu: true })),
  ...EUROPE_OTHER.map((code) => ({ code, region: 'EUROPE' as const, eu: false })),
  ...ASIA.map((code) => ({ code, region: 'ASIA' as const, eu: false })),
  ...OCEANIA.map((code) => ({ code, region: 'OCEANIA' as const, eu: false })),
  { code: 'AQ', region: 'ANTARCTICA', eu: false },
];

export const COUNTRY_CODES: ReadonlySet<string> = new Set(COUNTRIES.map((c) => c.code));
export const countryOf = (code: string): Country | undefined => COUNTRIES.find((c) => c.code === code);

/** Quick-pick groups for allow lists. */
export const COUNTRY_GROUPS: Record<string, { label: string; codes: string[] }> = {
  AFRICA: { label: 'Africa', codes: AFRICA },
  AMERICAS: { label: 'Americas (all)', codes: Object.values(AMERICAS).flat() },
  NORTH_AMERICA: { label: 'North America', codes: AMERICAS.NORTH },
  LATAM: { label: 'Latin America & Caribbean', codes: [...AMERICAS.CENTRAL, ...AMERICAS.CARIBBEAN, ...AMERICAS.SOUTH, 'MX'] },
  EU: { label: 'European Union (27)', codes: EU_MEMBERS },
  EUROPE: { label: 'Europe (all)', codes: [...EU_MEMBERS, ...EUROPE_OTHER] },
  ASIA: { label: 'Asia', codes: ASIA },
  OCEANIA: { label: 'Oceania', codes: OCEANIA },
};

/** A declared player / allow-list country: an assigned ISO 3166-1 alpha-2 code. */
export const IsoCountry = z.string().refine((c) => COUNTRY_CODES.has(c), { message: 'unknown ISO 3166-1 alpha-2 country code' });
